import { cn } from "@/lib/utils";
import {
    type CricketMatchRecordInning,
    formatCricketInningOver,
    formatCricketInningScore,
    parseCricketMatchRecord,
} from "@yn/utils";

interface CricketMatchRecordDisplayProps {
    matchRecord: string | null | undefined;
    showCompactInnings?: boolean;
    align?: "left" | "center" | "right";
    className?: string;
    regularInningClassName?: string;
    displayInningClassName?: string;
}

export function getCricketMatchRecordText( matchRecord: string | null | undefined ) {
    const parsedRecord = parseCricketMatchRecord( matchRecord );
    return parsedRecord.regularInning?.display ?? matchRecord ?? "";
}

function getDisplayMatchRecordInning( innings: CricketMatchRecordInning[] ) {
    return innings.length > 1 ? innings[innings.length - 1] : null;
}

function getCompactDisplayInningText( inning: CricketMatchRecordInning ) {
    const scoreText = formatCricketInningScore( inning );
    const overText = formatCricketInningOver( inning );

    return overText ? `${scoreText} (${overText})` : scoreText;
}

export function CricketMatchRecordDisplay(
    {
        align = "right",
        className,
        displayInningClassName,
        matchRecord,
        regularInningClassName,
        showCompactInnings = false,
    }: CricketMatchRecordDisplayProps,
) {
    const parsedRecord = parseCricketMatchRecord( matchRecord );
    const regularInning = parsedRecord.regularInning;
    const regularText = regularInning?.display ?? matchRecord ?? "";
    const displayInning = getDisplayMatchRecordInning( parsedRecord.innings );
    const shouldShowCompactInnings = showCompactInnings && !!displayInning && !!regularInning;

    if ( !regularText ) return null;

    if ( !shouldShowCompactInnings ) {
        return <span className={className}>{regularText}</span>;
    }

    return (
        <span
            className={cn(
                "inline-flex items-center gap-1 whitespace-nowrap leading-none",
                align === "center" && "items-center text-center",
                align === "left" && "items-start text-left",
                align === "right" && "items-end text-right",
                className,
            )}
        >
            <span className={cn( displayInningClassName )}>{getCompactDisplayInningText( displayInning )}</span>
            <span className="text-current">&amp;</span>
            <span className={regularInningClassName}>{formatCricketInningScore( regularInning )}</span>
        </span>
    );
}
